import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'

import { AddVendorModal } from '../components/AddVendorModal'
import { CreatePurchaseOrderModal } from '../components/CreatePurchaseOrderModal'
import { PurchaseOrderTable } from '../components/PurchaseOrderTable'
import { VendorTable } from '../components/VendorTable'
import { usePurchaseOrderStore } from '../store/purchaseOrder.store'
import { useVendorStore } from '../store/vendor.store'

export default function PurchasePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const activeTab =
    new URLSearchParams(location.search).get('tab') === 'orders'
      ? 'orders'
      : 'vendors'

  const locationSelectedVendor = location.state?.selectedVendor

  // State for search and modals
  const [search, setSearch] = useState('')
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false)
  const [isPurchaseOrderModalOpen, setIsPurchaseOrderModalOpen] = useState(
    Boolean(locationSelectedVendor),
  )
  const [selectedPoVendor, setSelectedPoVendor] = useState(
    locationSelectedVendor || null,
  )
  const [editingVendor, setEditingVendor] = useState(null)
  const [vendorToDelete, setVendorToDelete] = useState(null)

  // Stores
  const {
    vendors,
    isLoading: isLoadingVendors,
    isSubmitting: isSubmittingVendor,
    fetchVendors,
    createVendor,
    updateVendor,
    deleteVendor,
  } = useVendorStore()

  const {
    purchaseOrders,
    isLoading: isLoadingPurchaseOrders,
    isSubmitting: isSubmittingPurchaseOrder,
    fetchPurchaseOrders,
    createPurchaseOrder,
    fetchNextPurchaseOrderNumber,
  } = usePurchaseOrderStore()

  // Fetch initial vendors and orders
  useEffect(() => {
    fetchVendors().catch(() => {})
  }, [fetchVendors])

  useEffect(() => {
    fetchPurchaseOrders().catch(() => {})
  }, [fetchPurchaseOrders])

  // Filtered vendors (strictly by vendor / company name)
  const filteredVendors = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return vendors

    const matching = vendors.filter((vendor) => {
      const name = String(
        vendor.vendorName || vendor.companyName || '',
      ).toLowerCase()
      return name.includes(query)
    })

    return matching.sort((a, b) => {
      const nameA = String(a.vendorName || a.companyName || '').toLowerCase()
      const nameB = String(b.vendorName || b.companyName || '').toLowerCase()
      const startsWithA = nameA.startsWith(query)
      const startsWithB = nameB.startsWith(query)

      if (startsWithA && !startsWithB) return -1
      if (!startsWithA && startsWithB) return 1
      return nameA.localeCompare(nameB)
    })
  }, [vendors, search])

  // Filtered purchase orders
  const filteredPurchaseOrders = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return purchaseOrders

    return purchaseOrders.filter((order) =>
      [
        order.purchaseOrderNumber,
        order.vendor?.vendorName,
        order.paymentTerms,
        order.deliveryTerms,
        order.purchaseOrderDate,
      ]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(query)),
    )
  }, [purchaseOrders, search])

  // Vendor creation handler
  const handleCreateVendor = async (vendorPayload) => {
    try {
      const result = await createVendor(vendorPayload)
      if (result) {
        setIsVendorModalOpen(false)
        return true
      }
    } catch {
      // Store displays error toast
    }
    return false
  }

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor)
    setIsVendorModalOpen(true)
  }

  const handleUpdateVendor = async (vendorPayload) => {
    if (!editingVendor) return false

    try {
      const result = await updateVendor(editingVendor._id, vendorPayload)

      if (result) {
        setIsVendorModalOpen(false)
        setEditingVendor(null)
        return true
      }
    } catch {
      // Store displays error toast
    }

    return false
  }

  const handleDeleteVendor = (vendor) => {
    setVendorToDelete(vendor)
  }

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete) return

    try {
      await deleteVendor(vendorToDelete._id)
      setVendorToDelete(null)
    } catch {
      // Keep the modal open if deletion fails.
    }
  }

  // PO creation handlers
  const handleOpenCreatePoModal = (vendor = null) => {
    setSelectedPoVendor(vendor)
    setIsPurchaseOrderModalOpen(true)
  }

  const handleCloseCreatePoModal = () => {
    if (!isSubmittingPurchaseOrder) {
      setIsPurchaseOrderModalOpen(false)
      setSelectedPoVendor(null)

      if (location.state?.selectedVendor) {
        navigate(
          {
            pathname: location.pathname,
            search: location.search,
          },
          { replace: true, state: null },
        )
      }
    }
  }

  const handleCreatePurchaseOrder = async (poPayload) => {
    try {
      const result = await createPurchaseOrder(poPayload)
      if (result) {
        setIsPurchaseOrderModalOpen(false)
        setSelectedPoVendor(null)

        // Switch to orders tab
        navigate(
          {
            pathname: location.pathname,
            search: '?tab=orders',
          },
          { replace: true, state: null },
        )
      }
    } catch {
      // Store displays error toast
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      {activeTab === 'vendors' && (
        <VendorTable
          vendors={vendors}
          filteredVendors={filteredVendors}
          search={search}
          onSearchChange={setSearch}
          isLoading={isLoadingVendors}
          onOpenAddVendorModal={() => setIsVendorModalOpen(true)}
          onCreatePoForVendor={(vendor) => handleOpenCreatePoModal(vendor)}
          onEditVendor={handleEditVendor}
          onDeleteVendor={handleDeleteVendor}
          isAdmin={isAdmin}
        />
      )}

      {activeTab === 'orders' && (
        <PurchaseOrderTable
          purchaseOrders={purchaseOrders}
          filteredOrders={filteredPurchaseOrders}
          search={search}
          onSearchChange={setSearch}
          isLoading={isLoadingPurchaseOrders}
          onOpenCreatePoModal={() => handleOpenCreatePoModal(null)}
          isAdmin={isAdmin}
        />
      )}

      {/* Add Vendor Modal */}
      <AddVendorModal
        isOpen={isVendorModalOpen}
        onClose={() => {
          if (!isSubmittingVendor) {
            setIsVendorModalOpen(false)
            setEditingVendor(null)
          }
        }}
        initialVendor={editingVendor}
        onSubmit={editingVendor ? handleUpdateVendor : handleCreateVendor}
        isSubmitting={isSubmittingVendor}
      />

      {/* Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        isOpen={isPurchaseOrderModalOpen}
        onClose={handleCloseCreatePoModal}
        initialVendor={selectedPoVendor}
        vendors={vendors}
        purchaseOrders={purchaseOrders}
        onSubmit={handleCreatePurchaseOrder}
        isSubmitting={isSubmittingPurchaseOrder}
        fetchNextNumber={fetchNextPurchaseOrderNumber}
      />

      {/* Delete Vendor Confirmation Modal */}
      {vendorToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-vendor-title"
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-xl"
          >
            <h2
              id="delete-vendor-title"
              className="text-lg font-semibold text-gray-900"
            >
              Delete Vendor
            </h2>

            <p className="mt-2 text-sm text-gray-600 ">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{vendorToDelete.vendorName}</span>
              ? This action cannot be undone.
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setVendorToDelete(null)}
                className="w-full sm:w-auto rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteVendor}
                disabled={isSubmittingVendor}
                className="w-full sm:w-auto rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isSubmittingVendor ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
